/**
 * AtlasChronicleModule — the town Journal (V5 of the civilization layer).
 *
 * Press J near a settlement: the chronicle window opens on the town's durable
 * history (deaths, unions, births, verdicts, exiles, executions, rite
 * foundings, granary releases, festivals, residents) plus a STORY panel —
 * client-side sift patterns over the chronicle rows:
 *
 *   · Blood & the Law — each killing chained to the verdict that answered it
 *     (or flagged UNANSWERED — an open arc someone could pursue)
 *   · The Houses — unions/births grouped by surname (the rising families)
 *   · Milestones — foundings, policy acts, mentorships, new residents
 *
 * Citizenship v1 rides here too: "Take residence" leases a dwelling
 * (50 SOV / 7 days through the P3 ledger) and writes you into the chronicle.
 * Display only — the server is the authority on every row.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';

interface EconRow { settlementId: bigint; centerX: number; centerZ: number }
interface ChronRow { chronId: bigint; settlementId: bigint; kind: string; actorId: bigint; otherId: bigint; detail: string; at: { microsSinceUnixEpoch: bigint } }
interface ResidencyRow { identity: { isEqual(o: unknown): boolean }; settlementId: bigint; paidUntil: { microsSinceUnixEpoch: bigint } }
interface NameRow { settlementId: bigint; name: string }
interface Reducers { leaseResidency(a: { settlementId: bigint }): Promise<void> }

const KIND_BADGE: Record<string, { icon: string; color: string }> = {
  death: { icon: '†', color: 'var(--ax-bad)' },      // †
  execution: { icon: '⚖', color: 'var(--ax-bad)' },  // ⚖
  exile: { icon: '→', color: 'var(--ax-warn)' },      // →
  verdict: { icon: '⚖', color: 'var(--ax-warn)' },
  union: { icon: '❤', color: 'var(--ax-rare)' },      // ❤
  birth: { icon: '★', color: 'var(--ax-info)' },      // ★
  faith: { icon: '◈', color: 'var(--ax-rare)' },      // ◈
  rite: { icon: '◈', color: 'var(--ax-rare)' },
  granary: { icon: '☘', color: 'var(--ax-good)' },    // ☘
  festival: { icon: '♫', color: 'var(--ax-hi)' },   // ♫
  resident: { icon: '⌂', color: 'var(--ax-muted)' },   // ⌂
  mentor: { icon: '⚙', color: 'var(--ax-muted)' },     // ⚙
};

export class AtlasChronicleModule implements IModule {
  readonly name = 'AtlasChronicle';
  private win: UiWindow | null = null;
  private body: HTMLDivElement | null = null;
  private note: HTMLDivElement | null = null;
  private timer: number | null = null;
  private subscribedSid: bigint | null = null;

  init(_app: App): void {
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'KeyJ' || e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (atlas.loadingGate || atlas.currentWorldId === null || atlas.spaceFlight) return; // J = hyperdrive in space
      this.toggle();
    });
  }

  private tbl<T>(name: string): T[] {
    try {
      const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)[name];
      return t ? [...t.iter()] : [];
    } catch { return []; }
  }

  private nearestSid(): bigint | null {
    let best: bigint | null = null;
    let bestD = 1200; // the journal reads from the town's approach
    for (const e of this.tbl<EconRow>('settlementEcon')) {
      const d = Math.hypot(e.centerX - atlas.localX, e.centerZ - atlas.localZ);
      if (d < bestD) { best = e.settlementId; bestD = d; }
    }
    return best;
  }

  private sidHandle: { unsubscribe(): void } | null = null;
  private residencySubbed = false;
  private ensureSubs(sid: bigint): void {
    if (this.subscribedSid === sid) return;
    this.subscribedSid = sid;
    // (bug sweep) release the previous town's ~200-row chronicle query, and
    // subscribe the un-scoped residency table ONCE — every town visited used
    // to stack a fresh copy of both, with no release path.
    try { this.sidHandle?.unsubscribe(); } catch { /* gone */ }
    this.sidHandle = null;
    try {
      this.sidHandle = atlas.conn?.subscriptionBuilder().subscribe([
        `SELECT * FROM chronicle WHERE settlement_id = ${sid}`,
      ]) as { unsubscribe(): void } | null;
      if (!this.residencySubbed) {
        this.residencySubbed = true;
        atlas.conn?.subscriptionBuilder().subscribe(['SELECT * FROM residency']);
      }
    } catch { /* pre-publish server */ }
  }

  private toggle(): void {
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    const sid = this.nearestSid();
    if (sid === null) return; // no journal out in the wilds
    this.ensureSubs(sid);
    if (!this.win) {
      this.win = makeWindow({
        id: 'chronicle', title: 'Town Chronicle', icon: 'book',
        width: 520, height: 560, minWidth: 400, minHeight: 340, x: 110, y: 70,
      });
      const p = document.createElement('div');
      p.style.cssText = 'display:flex;flex-direction:column;height:100%;color:var(--ax-text);font:12px/1.5 var(--ax-font)';
      this.note = document.createElement('div');
      this.note.style.cssText = 'padding:6px 10px;border-bottom:1px solid var(--ax-line);color:var(--ax-soft);min-height:16px';
      p.appendChild(this.note);
      this.body = document.createElement('div');
      this.body.style.cssText = 'flex:1;overflow:auto;padding:8px 10px';
      p.appendChild(this.body);
      this.win.body.appendChild(p);
    }
    this.win.setOpen(true);
    if (this.timer === null) {
      this.timer = window.setInterval(() => {
        if (this.win?.isOpen()) this.render();
        else if (this.timer !== null) { window.clearInterval(this.timer); this.timer = null; }
      }, 1500);
    }
    this.render();
  }

  private esc(v: string): string {
    return v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
  }

  private say(text: string, err = false): void {
    if (this.note) { this.note.textContent = text; this.note.style.color = err ? 'var(--ax-bad)' : 'var(--ax-soft)'; }
  }

  private ago(at: { microsSinceUnixEpoch: bigint }): string {
    const s = Math.max(0, (Date.now() - Number(at.microsSinceUnixEpoch / 1000n)) / 1000);
    if (s < 90) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  }

  /** Sift: killings chained to the law's answer (or flagged unanswered). */
  private siftJustice(rows: ChronRow[]): string[] {
    const answers = rows.filter((r) => r.kind === 'execution' || r.kind === 'exile' || r.kind === 'verdict');
    const out: string[] = [];
    for (const d of rows.filter((r) => r.kind === 'death')) {
      const killer = d.otherId;
      const answer = answers.find((a) => a.actorId === killer && a.chronId > d.chronId);
      if (answer) {
        out.push(`<span style="color:var(--ax-bad)">†</span> ${this.esc(d.detail)}<br><span style="opacity:.75;padding-left:14px">↳ ${this.esc(answer.detail)}</span>`);
      } else {
        out.push(`<span style="color:var(--ax-bad)">†</span> ${this.esc(d.detail)}<br><span style="color:var(--ax-hi);padding-left:14px">↳ no verdict yet — the town still talks</span>`);
      }
      if (out.length >= 4) break;
    }
    return out;
  }

  /** Sift: houses by surname across unions + births. */
  private siftHouses(rows: ChronRow[]): string[] {
    const houses = new Map<string, { unions: number; births: number }>();
    for (const r of rows) {
      if (r.kind !== 'union' && r.kind !== 'birth') continue;
      const m = /the (\w+) household/.exec(r.detail);
      if (!m) continue;
      const h = houses.get(m[1]) ?? { unions: 0, births: 0 };
      if (r.kind === 'union') h.unions++; else h.births++;
      houses.set(m[1], h);
    }
    return [...houses.entries()]
      .sort((a, b) => (b[1].unions + b[1].births) - (a[1].unions + a[1].births))
      .slice(0, 4)
      .map(([name, h]) => `<span style="color:var(--ax-rare)">❤</span> House ${this.esc(name)} — ${h.unions} union${h.unions === 1 ? '' : 's'}, ${h.births} birth${h.births === 1 ? '' : 's'}`);
  }

  private render(): void {
    const b = this.body;
    const conn = atlas.conn;
    if (!b || !conn) return;
    const sid = this.nearestSid();
    if (sid === null) { b.innerHTML = '<div style="opacity:.7">No settlement in range — chronicles live in towns.</div>'; return; }
    this.ensureSubs(sid);
    const townName = this.tbl<NameRow>('settlementName').find((n) => n.settlementId === sid)?.name ?? `settlement #${sid}`;

    const rows = this.tbl<ChronRow>('chronicle')
      .filter((r) => r.settlementId === sid)
      .sort((x, y) => (x.chronId > y.chronId ? -1 : 1));

    const html: string[] = [];

    // Residency banner (citizenship v1).
    const me = conn.identity;
    const myRes = me ? this.tbl<ResidencyRow>('residency').find((r) => r.identity.isEqual(me)) : undefined;
    if (myRes && myRes.settlementId === sid && Number(myRes.paidUntil.microsSinceUnixEpoch / 1000n) > Date.now()) {
      const daysLeft = Math.max(0, Math.round((Number(myRes.paidUntil.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
      html.push(`<div style="padding:6px 8px;margin-bottom:8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-good)">⌂ You are a resident of ${this.esc(townName)} — ${daysLeft} day${daysLeft === 1 ? '' : 's'} on the lease. <button data-lease style="padding:1px 8px;margin-left:6px">Renew · 50 SOV</button></div>`);
    } else {
      html.push(`<div style="padding:6px 8px;margin-bottom:8px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-soft)">A traveler here. <button data-lease style="padding:1px 8px;margin-left:6px">Take residence · 50 SOV / 7 days</button></div>`);
    }

    // Stories (the sifter).
    const justice = this.siftJustice(rows);
    const houses = this.siftHouses(rows);
    if (justice.length + houses.length > 0) {
      html.push('<div style="font-weight:600;margin:4px 0 6px">Stories the town tells</div>');
      for (const s of justice) html.push(`<div style="padding:3px 0;border-bottom:1px solid var(--ax-line)">${s}</div>`);
      for (const s of houses) html.push(`<div style="padding:3px 0;border-bottom:1px solid var(--ax-line)">${s}</div>`);
    }

    // The annals.
    html.push(`<div style="font-weight:600;margin:12px 0 6px">The annals of ${this.esc(townName)}</div>`);
    if (rows.length === 0) {
      html.push('<div style="opacity:.6">The chronicle is young — nothing epochal has happened here yet. Deaths, unions, verdicts, foundings, and festivals will be written down.</div>');
    }
    for (const r of rows.slice(0, 60)) {
      const badge = KIND_BADGE[r.kind] ?? { icon: '•', color: 'var(--ax-muted)' };
      html.push(
        `<div style="display:flex;gap:8px;padding:3px 0;border-bottom:1px solid var(--ax-line)">` +
        `<span style="color:${badge.color};width:14px;text-align:center">${badge.icon}</span>` +
        `<span style="flex:1">${this.esc(r.detail)}</span>` +
        `<span style="opacity:.55;white-space:nowrap">${this.ago(r.at)}</span></div>`,
      );
    }
    b.innerHTML = html.join('');

    const leaseBtn = b.querySelector('[data-lease]');
    if (leaseBtn) {
      leaseBtn.addEventListener('click', () => {
        this.say('signing the lease…');
        const reducers = conn.reducers as unknown as Reducers;
        void reducers.leaseResidency({ settlementId: sid })
          .then(() => this.say('the chronicle records a new resident — welcome home'))
          .catch((err: Error) => this.say(String(err.message ?? err), true));
      });
    }
  }
}
